import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../commons/Header/Header";
import Sidebar from "../../../commons/Sidebar/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";

const formatCallDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** API `call_duration` is in minutes (e.g. 1.78). */
const formatCallDuration = (value) => {
  if (value === undefined || value === null || value === "") return "N/A";
  if (typeof value === "string" && /min|sec|hr|:/.test(value.toLowerCase())) {
    return value;
  }

  const minutesValue = Number(value);
  if (Number.isNaN(minutesValue) || minutesValue < 0) return String(value);

  const totalSeconds = Math.round(minutesValue * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")} Min ${String(seconds).padStart(2, "0")} Sec`;
};

const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const nestedMatch = trimmed.match(/amazonaws\.com\/(https%3A.+)$/i);
  if (nestedMatch?.[1]) {
    try {
      return decodeURIComponent(nestedMatch[1]);
    } catch {
      return trimmed;
    }
  }

  return trimmed;
};

const getMessageContent = (entry) => {
  if (!entry || typeof entry !== "object") {
    return typeof entry === "string" ? entry.trim() : "";
  }
  const value = entry.content ?? entry.message ?? entry.text ?? "";
  return typeof value === "string" ? value.trim() : "";
};

const getSpeakerLabel = (role) => {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "assistant" || normalized === "ai" || normalized === "agent" || normalized === "bot") {
    return "Ai Response";
  }
  if (normalized === "user" || normalized === "customer" || normalized === "caller") {
    return "You";
  }
  if (normalized === "system") return "System";
  return normalized ? normalized.replace(/\b\w/g, (c) => c.toUpperCase()) : "You";
};

/** Expected transcript: [{ role: "assistant"|"user", content: "..." }, ...] */
const parseConversation = (payload) => {
  let data = payload;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(data)) {
    if (Array.isArray(data?.transcript)) data = data.transcript;
    else if (Array.isArray(data?.messages)) data = data.messages;
    else return [];
  }

  return data
    .map((entry) => {
      const content = getMessageContent(entry);
      if (!content) return null;
      return {
        role: entry?.role || "user",
        content,
      };
    })
    .filter(Boolean);
};

const isJsonLikeResponse = (response, text) => {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) return true;
  const trimmed = String(text || "").trim();
  return trimmed.startsWith("[") || trimmed.startsWith("{");
};

const fetchConversation = async (url) => {
  const attempts = [
    `/s3-proxy?url=${encodeURIComponent(url)}`,
    url,
  ];

  let lastError;
  for (const requestUrl of attempts) {
    try {
      const response = await fetch(requestUrl);
      if (!response.ok) {
        lastError = new Error(`HTTP ${response.status}`);
        continue;
      }

      const text = await response.text();
      if (!isJsonLikeResponse(response, text)) {
        lastError = new Error("Response was not transcript JSON");
        continue;
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        lastError = error;
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Failed to load transcript");
};

function CallDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const call = location.state?.call;
  const backTo = location.state?.from || "/menu-and-restaurant-management";

  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [transcriptError, setTranscriptError] = useState("");

  const customerName = call?.customer?.full_name || "N/A";
  const customerMobile = call?.customer?.phone
    ? call?.customer?.country_code
      ? `+${call.customer.country_code} ${call.customer.phone}`
      : call.customer.phone
    : "N/A";

  const recordingUrl = useMemo(
    () => normalizeMediaUrl(call?.order_call_recording),
    [call?.order_call_recording]
  );
  const transcriptUrl = useMemo(
    () => normalizeMediaUrl(call?.order_transcript),
    [call?.order_transcript]
  );

  useEffect(() => {
    if (!call) {
      navigate(backTo, { replace: true });
    }
  }, [call, backTo, navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadTranscript = async () => {
      if (!transcriptUrl) {
        setConversation([]);
        setTranscriptError("");
        return;
      }

      setTranscriptLoading(true);
      setTranscriptError("");
      try {
        const payload = await fetchConversation(transcriptUrl);
        if (cancelled) return;

        const entries = parseConversation(payload);
        setConversation(entries);
        if (!entries.length) {
          setTranscriptError("No conversation found in transcript");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Transcript load error:", error);
          setConversation([]);
          setTranscriptError("Unable to load transcript");
        }
      } finally {
        if (!cancelled) setTranscriptLoading(false);
      }
    };

    loadTranscript();
    return () => {
      cancelled = true;
    };
  }, [transcriptUrl]);

  if (!call) return null;

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Call Detail</h3>
            </div>
            <div className="d-flex">
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-default rounded-lg"
                  onClick={() => navigate(backTo)}
                >
                  Back
                </button>
              </div>
            </div>
            <div className="row mx-n2 mt-4">
              <div className="col-md-4 p-2">
                <div className="rounded-14 border p-3">
                  <strong>Call Date</strong>
                  <p className="mb-0 mt-2">{formatCallDate(call?.createdAt)}</p>
                </div>
              </div>
              <div className="col-md-4 p-2">
                <div className="rounded-14 border p-3">
                  <strong>Customer Name</strong>
                  <p className="mb-0 mt-2">{customerName}</p>
                </div>
              </div>
              <div className="col-md-4 p-2">
                <div className="rounded-14 border p-3">
                  <strong>Customer Mobile</strong>
                  <p className="mb-0 mt-2">{customerMobile}</p>
                </div>
              </div>
              <div className="col-md-4 p-2">
                <div className="rounded-14 border p-3">
                  <strong>Call Duration</strong>
                  <p className="mb-0 mt-2">{formatCallDuration(call?.call_duration)}</p>
                </div>
              </div>
              <div className="col-md-4 p-2">
                <div className="rounded-14 border p-3">
                  <strong>Recording</strong>
                  <div className="mb-0 mt-2 d-flex">
                    {recordingUrl ? (
                      <audio
                        controls
                        style={{ width: "100%", height: "40px" }}
                        src={
                          process.env.NODE_ENV === "development"
                            ? `/s3-proxy?url=${encodeURIComponent(recordingUrl)}`
                            : recordingUrl
                        }
                      >
                        Your browser does not support the audio element.
                      </audio>
                    ) : (
                      <p className="mb-0 text-muted">N/A</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4 p-2">
                <div
                  className="rounded-14 border p-3"
                  style={{ height: "250px", overflow: "auto" }}
                >
                  {transcriptLoading ? (
                    <p className="mb-0 mt-2 text-muted">Loading transcript...</p>
                  ) : conversation.length > 0 ? (
                    conversation.map((entry, index) => (
                      <div key={`${entry.role}-${index}`} className={index === 0 ? "" : "mt-2"}>
                        <strong>{getSpeakerLabel(entry.role)}</strong>
                        <p className="mb-0">{entry.content}</p>
                      </div>
                    ))
                  ) : (
                    <div>
                      <p className="mb-2 text-muted">{transcriptError || "N/A"}</p>
                      {transcriptUrl ? (
                        <a
                          href={transcriptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          Open transcript
                        </a>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallDetail;
